import React, { useEffect, useState } from "react";
import Card from "../../theme/untitled/Card/Card";
import Button from "../../theme/untitled/Button/Button";
import Input from "../../theme/untitled/Input/Input";
import Icon from "../../theme/untitled/icons/Icon";
import { getUploadResourceUrl } from "../../../../helpers/url";
import styles from "./WorkspacePage.module.scss";
import { parseGeneralError } from "./errorHelpers";

const REQUIRED_MESSAGE =
  "Enter a workspace name — members need something to recognise it by.";

const BasicInformationCard = ({
  orgModel,
  currentOrganization,
  editOrganization,
}) => {
  const baselineName = orgModel.name || "";
  const [name, setName] = useState(baselineName);
  const [justSaved, setJustSaved] = useState(false);

  const loading = currentOrganization.edit;
  const isDirty = name !== baselineName;

  useEffect(() => {
    if (currentOrganization.editSuccess) {
      setName(orgModel.name || "");
      setJustSaved(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization.editSuccess]);

  const generalError = currentOrganization.editError
    ? parseGeneralError(
        currentOrganization.editError,
        "The service is unavailable. Your edits are still here — try again in a moment."
      )
    : null;

  const trimmed = name.trim();
  const isRequiredViolation = isDirty && trimmed.length === 0;
  const isValid = trimmed.length >= 2 && trimmed.length <= 64;

  const onChangeName = (e) => {
    setJustSaved(false);
    setName(e.target.value);
  };

  const onCancel = () => {
    setName(baselineName);
    setJustSaved(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    editOrganization(orgModel.uuid, { name: trimmed });
  };

  const saveDisabled = loading || !isDirty || !isValid;
  const cancelDisabled = loading || !isDirty;

  const logoUrl = orgModel.settings?.logo
    ? getUploadResourceUrl(orgModel.settings.logo)
    : null;

  return (
    <Card aria-labelledby="workspace-basic-information-title">
      <Card.Header
        id="workspace-basic-information-title"
        title="Basic information"
        subtitle="Used in the workspace switcher, and in public-facing page titles for your widgets."
      />
      <form onSubmit={onSubmit}>
        <Card.Body>
          <div className={styles.identity}>
            <span className={styles.logoTile}>
              {logoUrl ? (
                <img className={styles.logoTileImage} src={logoUrl} alt="" />
              ) : (
                <Icon name="Building02" size={34} />
              )}
            </span>
            <div className={styles.identityName}>{baselineName}</div>
          </div>

          <div className={styles.grid}>
            <Input
              label={
                <>
                  Workspace name <span className={styles.required}>*</span>
                </>
              }
              value={name}
              onChange={onChangeName}
              readOnly={loading}
              maxLength={64}
              hint={
                isRequiredViolation
                  ? undefined
                  : "Shown to every member of this workspace."
              }
              error={isRequiredViolation ? REQUIRED_MESSAGE : undefined}
            />
          </div>
        </Card.Body>

        <Card.Footer className={styles.cardFooter}>
          {generalError && (
            <span
              className={`${styles.footerMessage} ${styles["footerMessage--error"]}`}
            >
              <Icon
                name="AlertCircle"
                size={16}
                className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--error"]}`}
              />
              <span>
                <strong>Couldn't save your changes.</strong> {generalError}
              </span>
            </span>
          )}
          {!generalError && isRequiredViolation && (
            <span
              className={`${styles.footerMessage} ${styles["footerMessage--error"]}`}
            >
              <Icon
                name="AlertCircle"
                size={16}
                className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--error"]}`}
              />
              <span>
                <strong>Changes were not saved.</strong> Fix the field above and
                try again.
              </span>
            </span>
          )}
          {!generalError && !isRequiredViolation && justSaved && !isDirty && (
            <span
              className={`${styles.footerMessage} ${styles["footerMessage--success"]}`}
            >
              <Icon
                name="CheckCircle"
                size={16}
                className={`${styles.footerMessageIcon} ${styles["footerMessageIcon--success"]}`}
              />
              <span>Saved just now</span>
            </span>
          )}

          <div className={styles.cardFooterActions}>
            <Button
              hierarchy="secondaryGray"
              disabled={cancelDisabled}
              onClick={onCancel}
              type="button"
            >
              Restore
            </Button>
            <Button
              hierarchy="primary"
              type="submit"
              disabled={saveDisabled}
              loading={loading}
            >
              {loading
                ? "Saving…"
                : generalError
                ? "Try again"
                : "Save changes"}
            </Button>
          </div>
        </Card.Footer>
      </form>
    </Card>
  );
};

export default BasicInformationCard;
